// frontend/src/widgets/QuoteLineItemsWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-01-27
// Updated:      2026-09-09 14:28 BST
//
// Description:
//   Quote line items widget.
//   - Loads items via GET /api/quotes/{id}/line-items
//   - Adds via POST /api/quotes/{id}/line-items
//   - Updates via PATCH /api/quotes/{id}/line-items/{line_item_id}
//   - Deletes via DELETE /api/quotes/{id}/line-items/{line_item_id}
//   - Guards all mutations using /status/meta line_items_editable (draft/submitted only)
//
// IMPORTANT:
//   Uses shared Axios instance ONLY.
//
// Hotfix (2026-02-10):
//   - Fix: Never send trailing-slash IDs to backend (prevents 307 redirect weirdness on DELETE in browser).
//   - Fix: Keep original "/api/..." URL style to avoid accidental "/api/api" double-prefix depending on axios baseURL.
//
// Mobile Responsiveness (2026-09-09):
//   - Preserves all line-item GET/POST/PATCH/DELETE behaviour and status/meta edit guards.
//   - New-line-item controls stack vertically on mobile and retain the 3-column desktop layout.
//   - Existing line items remain cards; editable/read-only field grids collapse cleanly on phones.
//   - Delete and Add controls become full-width touch targets on mobile.
//   - Mobile records viewport shows one complete line-item card per scroll/snap.
//   - Dynamic mobile growth uses the proven Vision pattern:
//       restore previous host sizing;
//       clear temporary viewport/root sizing;
//       measure the natural first line-item card;
//       set the records viewport to exactly one full card;
//       wait a second animation frame;
//       resize the first width-matching fixed-height mobile host.
//   - ResizeObserver watches the first line-item card; header/error/form height
//     changes retrigger the sizing effect through existing component state.
//   - No API routes, payloads, safe-id handling, tax-rate mapping, blur/Enter commits,
//     editability guards or caller callbacks are changed.
//
// Operational Notes:
//   - One render tree is shared by desktop and mobile.
//   - Desktop keeps its existing natural full-list behaviour.
//
// VAT Default Fix (2026-09-09):
//   - New quote line items now deterministically default to 20% VAT.
//   - When the tax-rate catalogue contains a 20% row, that row is selected.
//   - If tax-rate lookup is unavailable, the numeric fallback remains 0.20.
//   - Reset after a successful Add returns the new-item form to 20% VAT.
//   - Explicit user selection of another tax rate remains unchanged.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "../context/ThemeContext.jsx";
import api from "../api/axiosInstance";

// ---- helpers ---------------------------------------------------------------
const DEBUG = false;
const DEFAULT_TAX_RATE = 0.2;

const norm = (v) => String(v ?? "").trim();
const stripTrailingSlashes = (v) => norm(v).replace(/\/+$/, "");
const safeId = (v) => {
  const s = stripTrailingSlashes(v);
  return s ? s : "";
};

const toNum = (v, fallback = 0) => {
  const s = norm(v);
  if (!s) return fallback;
  const n = Number(s);
  return Number.isFinite(n) ? n : fallback;
};

const axiosErr = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;

// Normalize tax_rate to a number (backend expects numeric like 0.2)
const toTaxRateNum = (v) => {
  if (v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Find best matching tax rate option by numeric rate (tolerant to rounding)
const findTaxOptionByRate = (taxRates, rate) => {
  const r = toTaxRateNum(rate);
  if (!Array.isArray(taxRates) || taxRates.length === 0) return null;

  let best = null;
  let bestDelta = Infinity;

  for (const t of taxRates) {
    const tr = toTaxRateNum(t?.rate);
    const d = Math.abs(tr - r);
    if (d < bestDelta) {
      bestDelta = d;
      best = t;
    }
  }

  // If it's not close, treat as "custom" (no selection)
  if (bestDelta > 0.0001) return null;
  return best;
};

// ----------------------------------------------------------------------------

export default function QuoteLineItemsWidget({ quoteId, refreshKey, onLineItemsChanged }) {
  const { theme } = useTheme();

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);

  const [taxRates, setTaxRates] = useState([]);
  const [taxRatesErr, setTaxRatesErr] = useState("");

  const [newDesc, setNewDesc] = useState("");
  const [newQty, setNewQty] = useState(1);
  const [newPrice, setNewPrice] = useState(0);

  // tax UI: dropdown uses tax_rate_id, but payload uses numeric tax_rate
  const [newTaxRateId, setNewTaxRateId] = useState(""); // "" or numeric string
  const [newTaxCustom, setNewTaxCustom] = useState(String(DEFAULT_TAX_RATE)); // fallback when no catalogue match

  const widgetRootRef = useRef(null);
  const recordsViewportRef = useRef(null);
  const firstItemRef = useRef(null);

  const qid = useMemo(() => safeId(quoteId), [quoteId]);

  const card = useMemo(
    () => ({
      background: "#fff",
      borderRadius: 12,
      padding: 14,
      boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
      minHeight: 0,
      boxSizing: "border-box",
      overflowWrap: "anywhere",
    }),
    []
  );

  const titleStyle = useMemo(
    () => ({
      fontWeight: 800,
      marginBottom: 10,
      fontFamily: theme?.font_secondary || theme?.font_primary || "inherit",
    }),
    [theme]
  );

  const input = useMemo(
    () => ({
      width: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      padding: "10px 10px",
      borderRadius: 10,
      border: "1px solid #e5e7eb",
      outline: "none",
      fontFamily: theme?.font_primary || "inherit",
    }),
    [theme]
  );

  const select = useMemo(
    () => ({
      ...input,
      background: "#fff",
      cursor: "pointer",
    }),
    [input]
  );

  const button = useMemo(
    () => ({
      border: 0,
      borderRadius: 10,
      padding: "10px 12px",
      fontWeight: 800,
      cursor: "pointer",
      fontFamily: theme?.font_primary || "inherit",
      background: theme?.button_action?.normal || theme?.primary_color || "#111827",
      color: "#fff",
    }),
    [theme]
  );

  const buttonDisabled = useMemo(
    () => ({
      ...button,
      background: "#e5e7eb",
      color: "#6b7280",
      cursor: "not-allowed",
    }),
    [button]
  );

  const urls = useMemo(() => {
    if (!qid) return { itemsUrl: "", metaUrl: "", taxRatesUrl: "" };
    return {
      itemsUrl: `/api/quotes/${qid}/line-items`,
      metaUrl: `/api/quotes/${qid}/status/meta`,
      taxRatesUrl: `/api/quote-catalog/tax-rates`,
    };
  }, [qid]);

  const loadTaxRatesBestEffort = async () => {
    setTaxRatesErr("");
    try {
      // best-effort: quotes module already uses quote-catalog tax rates
      const res = await api.get(urls.taxRatesUrl, { params: { include_inactive: false } });
      const arr = Array.isArray(res.data) ? res.data : [];
      setTaxRates(arr);
    } catch (e) {
      setTaxRates([]);
      setTaxRatesErr(axiosErr(e, "Failed to load tax rates"));
    }
  };

  const load = async () => {
    if (!qid) {
      setItems([]);
      setMeta(null);
      setErr("");
      return;
    }

    setLoading(true);
    setErr("");

    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log("[QuoteLineItemsWidget] load()", urls);
    }

    try {
      const [iRes, mRes] = await Promise.all([api.get(urls.itemsUrl), api.get(urls.metaUrl)]);
      setItems(Array.isArray(iRes.data) ? iRes.data : []);
      setMeta(mRes.data || null);
    } catch (e) {
      setErr(axiosErr(e, "Failed to load line items"));
      setItems([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!qid) return;
    loadTaxRatesBestEffort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qid]);

  useEffect(() => {
    if (!qid || !taxRates.length) return;

    const defaultTaxOption = findTaxOptionByRate(
      taxRates,
      DEFAULT_TAX_RATE
    );

    if (defaultTaxOption?.id == null) return;

    setNewTaxRateId((current) =>
      current ? current : String(defaultTaxOption.id)
    );
  }, [qid, taxRates]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qid, refreshKey]);

  const editable = Boolean(meta?.line_items_editable);

  const computeNewTaxRate = () => {
    // If dropdown has value, use its numeric rate. Otherwise use custom numeric input.
    if (newTaxRateId) {
      const t = taxRates.find((x) => String(x?.id) === String(newTaxRateId));
      return toTaxRateNum(t?.rate);
    }
    return toTaxRateNum(newTaxCustom);
  };

  const resetNew = () => {
    const defaultTaxOption = findTaxOptionByRate(
      taxRates,
      DEFAULT_TAX_RATE
    );

    setNewDesc("");
    setNewQty(1);
    setNewPrice(0);
    setNewTaxRateId(
      defaultTaxOption?.id != null
        ? String(defaultTaxOption.id)
        : ""
    );
    setNewTaxCustom(String(DEFAULT_TAX_RATE));
  };

  const add = async () => {
    if (!qid) return;

    const d = String(newDesc || "").trim();
    if (!d) {
      setErr("description is required");
      return;
    }

    const payload = {
      description: d,
      quantity: toNum(newQty, 1) || 1,
      unit_price: toNum(newPrice, 0) || 0,
      tax_rate: computeNewTaxRate(),
      sort_order: 0,
    };

    setErr("");

    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log("[QuoteLineItemsWidget] add()", { url: urls.itemsUrl, payload });
    }

    try {
      await api.post(urls.itemsUrl, payload);
      resetNew();
      await load();
      onLineItemsChanged?.();
    } catch (e) {
      setErr(axiosErr(e, "Failed to add line item"));
    }
  };

  const patch = async (id, updates) => {
    if (!qid) return;

    const lid = safeId(id);
    if (!lid) {
      setErr("Invalid line item id");
      return;
    }

    setErr("");

    const url = `/api/quotes/${qid}/line-items/${lid}`;

    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log("[QuoteLineItemsWidget] patch()", { url, updates });
    }

    try {
      await api.patch(url, updates);
      await load();
      onLineItemsChanged?.();
    } catch (e) {
      setErr(axiosErr(e, "Failed to update line item"));
    }
  };

  const remove = async (id) => {
    if (!qid) return;

    const lid = safeId(id);
    if (!lid) {
      setErr("Invalid line item id");
      return;
    }

    setErr("");

    // Critical: no trailing slash
    const url = `/api/quotes/${qid}/line-items/${lid}`;

    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log("[QuoteLineItemsWidget] delete()", { url });
    }

    try {
      await api.delete(url);
      await load();
      onLineItemsChanged?.();
    } catch (e) {
      setErr(axiosErr(e, "Failed to delete line item"));
    }
  };

  const taxDropdownAvailable = taxRates.length > 0;

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const root = widgetRootRef.current;
    const viewport = recordsViewportRef.current;
    const firstItem = firstItemRef.current;

    if (!root || !viewport) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 767px)");

    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;

      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];

      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();

        const widthMatches =
          Math.abs(rect.width - rootRect.width) < 90;

        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length ? candidates[0] : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      viewport.style.height = "";
      viewport.style.minHeight = "";
      root.style.height = "";
      root.style.minHeight = "";

      if (!mediaQuery.matches) {
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        const currentFirstItem = firstItemRef.current;

        if (currentFirstItem) {
          const itemHeight = Math.ceil(
            Math.max(
              currentFirstItem.getBoundingClientRect().height || 0,
              currentFirstItem.scrollHeight || 0
            )
          );

          if (Number.isFinite(itemHeight) && itemHeight > 0) {
            viewport.style.height = `${itemHeight}px`;
            viewport.style.minHeight = `${itemHeight}px`;
          }
        }

        root.style.height = "auto";
        root.style.minHeight = "0px";

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();

          if (!host) {
            return;
          }

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();

          const wrapperChromeHeight = Math.max(
            0,
            rootRect.top - hostRect.top
          );

          const requiredHostHeight = Math.ceil(
            Math.max(
              host.scrollHeight,
              wrapperChromeHeight + rootRect.height + 8
            )
          );

          if (
            !Number.isFinite(requiredHostHeight) ||
            requiredHostHeight <= 0
          ) {
            return;
          }

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyMobileSizing())
        : null;

    if (resizeObserver && firstItem) {
      resizeObserver.observe(firstItem);
    }

    const handleMediaChange = () => applyMobileSizing();

    mediaQuery.addEventListener?.(
      "change",
      handleMediaChange
    );

    window.addEventListener(
      "resize",
      applyMobileSizing
    );

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();

      mediaQuery.removeEventListener?.(
        "change",
        handleMediaChange
      );

      window.removeEventListener(
        "resize",
        applyMobileSizing
      );

      restoreHost();

      viewport.style.height = "";
      viewport.style.minHeight = "";
      root.style.height = "";
      root.style.minHeight = "";
    };
  }, [
    editable,
    err,
    items.length,
    loading,
    qid,
    taxDropdownAvailable,
    taxRatesErr,
  ]);

  return (
    <div
      ref={widgetRootRef}
      className="h-auto min-h-0 w-full overflow-visible md:min-h-[320px]"
      style={card}
    >
      <style>{`
        @media (max-width: 767px) {
          .vx-quote-line-items-viewport {
            overflow-y: auto !important;
            overflow-x: hidden !important;
            overscroll-behavior-y: contain;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }

          .vx-quote-line-items-viewport::-webkit-scrollbar {
            display: none;
          }
        }
      `}</style>

      <div style={titleStyle}>Line Items</div>

      {!qid ? <div style={{ color: "#6b7280", fontWeight: 800 }}>Select a quote to view line items.</div> : null}

      {err ? <div className="break-words" style={{ color: "#b91c1c", fontWeight: 800, marginBottom: 10, overflowWrap: "anywhere" }}>{err}</div> : null}
      {taxRatesErr ? <div className="break-words" style={{ color: "#b91c1c", fontWeight: 800, marginBottom: 10, overflowWrap: "anywhere" }}>{taxRatesErr}</div> : null}
      {loading ? <div style={{ fontWeight: 800 }}>Loading…</div> : null}

      {qid ? (
        <>
          <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900, marginBottom: 10 }}>
            Editable: {editable ? "Yes (draft/submitted)" : "No (locked by status)"}
          </div>

          <div style={{ display: "grid", gap: 10, marginBottom: 12 }}>
            <input
              style={input}
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder="Description"
              disabled={!editable}
            />

            <div className="grid grid-cols-1 gap-2.5 md:grid-cols-3">
              <input
                style={input}
                type="number"
                value={newQty}
                onChange={(e) => setNewQty(e.target.value)}
                placeholder="Qty"
                disabled={!editable}
              />
              <input
                style={input}
                type="number"
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
                placeholder="Unit price"
                disabled={!editable}
              />

              {taxDropdownAvailable ? (
                <select
                  style={select}
                  value={newTaxRateId}
                  onChange={(e) => setNewTaxRateId(e.target.value)}
                  disabled={!editable}
                  title="Tax rate"
                >
                  <option value="">Tax: none / 0%</option>
                  {taxRates
                    .slice()
                    .sort((a, b) => Number(a?.id) - Number(b?.id))
                    .map((t) => (
                      <option key={t.id} value={String(t.id)}>
                        {t?.label || t?.code || `Tax #${t.id}`} ({Math.round(toTaxRateNum(t?.rate) * 10000) / 100}%)
                      </option>
                    ))}
                </select>
              ) : (
                <input
                  style={input}
                  type="number"
                  value={newTaxCustom}
                  onChange={(e) => setNewTaxCustom(e.target.value)}
                  placeholder="Tax rate (e.g. 0.2)"
                  disabled={!editable}
                />
              )}
            </div>

            <button
              type="button"
              className="min-h-[44px] w-full md:w-auto"
              onClick={add}
              style={editable ? button : buttonDisabled}
              disabled={!editable}
            >
              Add line item
            </button>
          </div>

          <div
            ref={recordsViewportRef}
            className="vx-quote-line-items-viewport grid gap-2 overflow-visible md:h-auto md:overflow-visible"
            style={{ scrollSnapType: "y mandatory" }}
          >
            {items.map((li, index) => (
              <div
                key={li.id}
                ref={index === 0 ? firstItemRef : null}
                className="min-w-0 md:block"
                style={{ scrollSnapAlign: "start", scrollSnapStop: "always" }}
              >
                <LineItemRow
                  li={li}
                  editable={editable}
                  inputStyle={input}
                  selectStyle={select}
                  button={button}
                  buttonDisabled={buttonDisabled}
                  theme={theme}
                  taxRates={taxRates}
                  patch={patch}
                  remove={remove}
                />
              </div>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

function FieldReadOnly({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>{label}</div>
      <div style={{ fontWeight: 900 }}>{value ?? "—"}</div>
    </div>
  );
}

function LineItemRow({
  li,
  editable,
  inputStyle,
  selectStyle,
  button,
  buttonDisabled,
  theme,
  taxRates,
  patch,
  remove,
}) {
  // Local drafts to avoid PATCH + reload on every keypress (prevents focus loss)
  const [descDraft, setDescDraft] = useState(li.description ?? "");
  const [qtyDraft, setQtyDraft] = useState(li.quantity ?? 1);
  const [priceDraft, setPriceDraft] = useState(li.unit_price ?? 0);

  // Dropdown stores tax_rate_id; payload still sends numeric tax_rate
  const [taxRateIdDraft, setTaxRateIdDraft] = useState("");
  const [taxCustomDraft, setTaxCustomDraft] = useState(String(toTaxRateNum(li.tax_rate)));

  const taxDropdownAvailable = Array.isArray(taxRates) && taxRates.length > 0;

  useEffect(() => {
    setDescDraft(li.description ?? "");
    setQtyDraft(li.quantity ?? 1);
    setPriceDraft(li.unit_price ?? 0);

    const opt = findTaxOptionByRate(taxRates, li.tax_rate);
    setTaxRateIdDraft(opt ? String(opt.id) : "");
    setTaxCustomDraft(String(toTaxRateNum(li.tax_rate)));
  }, [li.id, li.description, li.quantity, li.unit_price, li.tax_rate, taxRates]);

  const commitDesc = async () => {
    const next = String(descDraft ?? "").trim();
    const cur = String(li.description ?? "").trim();
    if (!next || next === cur) return;
    await patch(li.id, { description: next });
  };

  const commitQty = async () => {
    const next = toNum(qtyDraft, 1) || 1;
    const cur = toNum(li.quantity, 1) || 1;
    if (next === cur) return;
    await patch(li.id, { quantity: next });
  };

  const commitPrice = async () => {
    const next = toNum(priceDraft, 0) || 0;
    const cur = toNum(li.unit_price, 0) || 0;
    if (next === cur) return;
    await patch(li.id, { unit_price: next });
  };

  const commitTaxFromDropdown = async (rawId) => {
    if (!rawId) {
      const cur = toTaxRateNum(li.tax_rate);
      if (cur === 0) return;
      await patch(li.id, { tax_rate: 0 });
      return;
    }
    const t = taxRates.find((x) => String(x?.id) === String(rawId));
    const next = toTaxRateNum(t?.rate);
    const cur = toTaxRateNum(li.tax_rate);
    if (Math.abs(next - cur) < 0.0001) return;
    await patch(li.id, { tax_rate: next });
  };

  const commitTaxFromCustom = async () => {
    const next = toTaxRateNum(taxCustomDraft);
    const cur = toTaxRateNum(li.tax_rate);
    if (Math.abs(next - cur) < 0.0001) return;
    await patch(li.id, { tax_rate: next });
  };

  return (
    <div
      className="min-w-0"
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 12,
        display: "grid",
        gap: 8,
        overflowWrap: "anywhere",
      }}
    >
      <div className="grid min-w-0 grid-cols-1 gap-2.5 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
        <div style={{ fontWeight: 900 }}>
          {li.description} <span style={{ fontWeight: 800, color: "#6b7280" }}> (#{li.id})</span>
        </div>

        <button
          type="button"
          className="min-h-[44px] w-full sm:w-auto"
          onClick={() => remove(li.id)}
          disabled={!editable}
          style={editable ? { ...button, background: theme?.button_general?.normal || "#111827" } : buttonDisabled}
        >
          Delete
        </button>
      </div>

      {editable ? (
        <div style={{ display: "grid", gap: 10 }}>
          <div>
            <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Description</div>
            <input
              style={inputStyle}
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
              onBlur={commitDesc}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
              }}
            />
          </div>

          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Qty</div>
              <input
                type="number"
                value={qtyDraft}
                onChange={(e) => setQtyDraft(e.target.value)}
                onBlur={commitQty}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                }}
                style={inputStyle}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Unit price</div>
              <input
                type="number"
                value={priceDraft}
                onChange={(e) => setPriceDraft(e.target.value)}
                onBlur={commitPrice}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                }}
                style={inputStyle}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>Tax</div>
              {taxDropdownAvailable ? (
                <select
                  style={selectStyle}
                  value={taxRateIdDraft}
                  onChange={(e) => {
                    setTaxRateIdDraft(e.target.value);
                    commitTaxFromDropdown(e.target.value);
                  }}
                >
                  <option value="">(none / 0%)</option>
                  {taxRates
                    .slice()
                    .sort((a, b) => Number(a?.id) - Number(b?.id))
                    .map((t) => (
                      <option key={t.id} value={String(t.id)}>
                        {t?.label || t?.code || `Tax #${t.id}`} ({Math.round(toTaxRateNum(t?.rate) * 10000) / 100}%)
                      </option>
                    ))}
                </select>
              ) : (
                <input
                  style={inputStyle}
                  type="number"
                  value={taxCustomDraft}
                  onChange={(e) => setTaxCustomDraft(e.target.value)}
                  onBlur={commitTaxFromCustom}
                  placeholder="e.g. 0.2"
                />
              )}
            </div>

            <FieldReadOnly label="Line total" value={li.line_total ?? "—"} />
          </div>

          <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>
            Subtotal: {li.line_subtotal ?? "—"} · Tax: {li.line_tax ?? "—"}
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
            <FieldReadOnly label="Qty" value={li.quantity} />
            <FieldReadOnly label="Unit price" value={li.unit_price} />
            <FieldReadOnly label="Tax rate" value={li.tax_rate} />
            <FieldReadOnly label="Line total" value={li.line_total ?? "—"} />
          </div>

          <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 900 }}>
            Subtotal: {li.line_subtotal ?? "—"} · Tax: {li.line_tax ?? "—"}
          </div>
        </>
      )}
    </div>
  );
}
